// What the Notes screen works out for itself, and nothing more.
//
// Pure - no React, no network - the same split `day/day-model.ts` and
// `edit/edit-model.ts` keep, so the rules can be read and tested without a
// screen around them.
//
// THE LINE THIS FILE IS ON. What a note IS - what it is called when it has no
// title, which notes match what you typed, which is the newest - is the
// SERVER'S, and arrives already decided (`heading`, the order of `notes`, the
// list a `search` comes back with). What is here is how this screen DRAWS that:
// a date in the words a person uses, a preview on one line, a running word
// count that changes as you type and so could never come from a round trip.

import type { IsoDay, Note, NoteSection } from '../api/client.ts';
import { addDays, dayAndMonth, parseIsoDay, weekdayName } from '../iso-day.ts';
import { asProse } from './note-markdown.ts';

/** How many days back the weekday alone still says which day it was. */
const WEEKDAY_WINDOW = 6;

/**
 * When a note was last changed, in the words the approved design uses:
 * `Today`, `Yesterday`, `Monday`, `3 August`, `3 August 2025`.
 *
 * BOTH ARGUMENTS ARE CALENDAR DAYS the server sent, so this is string
 * arithmetic and no clock or zone is consulted - the day it happened on was
 * decided once, on the server, in the user's own zone (AGENTS.md).
 *
 * The prototype's own labels carry a time as well ("Today, 8:42"). A time is a
 * second time-zone decision and there is nothing in the payload that could make
 * it honestly, so it is left out rather than guessed at from this machine's
 * clock. Whoever adds it adds it to the payload first.
 */
export function whenLabel(day: IsoDay, today: IsoDay): string {
  if (parseIsoDay(day) === null) return '';
  if (day === today) return 'Today';
  if (day === addDays(today, -1)) return 'Yesterday';

  // Only backwards. A day after today cannot be described as a weekday just
  // gone, and nothing should ever produce one - so it falls through to a date
  // rather than being quietly named something wrong.
  if (day < today && day >= addDays(today, -WEEKDAY_WINDOW)) return weekdayName(day);

  const when = parseIsoDay(day);
  const now = parseIsoDay(today);
  const sameYear = when !== null && now !== null && when.year === now.year;
  return sameYear ? dayAndMonth(day) : `${dayAndMonth(day)} ${when?.year ?? ''}`.trim();
}

/**
 * A note's body on one line, for the two-line preview under its heading.
 *
 * The line breaks a person wrote are what makes a note readable and what makes
 * a preview ragged, so they become spaces here and are untouched everywhere
 * else. The prototype does exactly this, for the same reason.
 *
 * THE BLOCK MARKERS COME OFF FIRST, and flattening is exactly why. `#`, `>` and
 * `- [ ]` structure the LINE they start; once every line is on one line there is
 * no structure left for them to carry, and they are read as `# Monday plan - [ ]
 * call the bank` - punctuation from a syntax the preview is not showing.
 * `asProse` in `note-markdown.ts` is the one place that knows which markers
 * those are; inline ones are deliberately left alone there.
 */
export function previewOf(body: string): string {
  return asProse(body).replace(/\s+/g, ' ').trim();
}

/**
 * Where a routine note lives, in words: on a reminder, or on an item in a
 * named schedule.
 *
 * Null for a note of its own - it is not anywhere, which is the whole of what
 * makes it free-standing, and a row saying so would be a row saying nothing.
 * That absence is the difference between the two kinds on the list, so this
 * says "Item in Gym" rather than "Gym": the schedule name alone would read as a
 * folder, and there are no folders here.
 *
 * `archived` is said out loud rather than left to be noticed. An archived item
 * is off the day, so its note is on this screen and the thing it is about is
 * not - and a note that quietly stopped matching anything would be worse.
 *
 * NOT AFFECTED BY NOTE GROUPS, and the sentence above about folders is still
 * true of what this returns. A group is a heading with the note UNDER it, so the
 * group never needs saying on the row; what this says is that the row is not a
 * note at all but a note written on something, which no heading can carry.
 */
export function whereLabel(note: Note): string | null {
  if (note.kind !== 'item') return null;
  const place = note.schedule === null ? 'Reminder' : `Item in ${note.schedule.name}`;
  return note.archived ? `${place} - archived` : place;
}

/**
 * Whether this row can be pinned and filed at all.
 *
 * FALSE FOR AN ITEM NOTE, and it is the server's shape rather than a rule this
 * client invented: an item note is `routine.note`, and `routine` carries neither
 * `pinned` nor `note_section_id`. The payload says so - `pinned` is always false
 * and `noteSectionId` always null on one - and this is the one place the screen
 * asks, so the controls that would refuse are simply not drawn.
 *
 * An item note is organised by routine ownership instead: `groupNotes` puts a
 * reminder under Reminder and a scheduled item under Schedules, while
 * `whereLabel` names the specific owner on the row.
 */
export function canOrganise(note: Note): boolean {
  return note.kind === 'note';
}

/** The collapse key of the pinned area. Not a section, so not an id. */
export const PINNED_KEY = 'pinned';

/** The collapse key of everything in no group. Likewise. */
export const LOOSE_KEY = 'loose';

/**
 * The two automatic blocks. These are IDENTITIES, not display names: grouping
 * is derived from a routine's durable ownership (`kind` plus `schedule`) and a
 * user-created section called "Reminder" or "Schedules" remains a separate
 * block with its own uuid. No database rows are needed, so concurrent reads or
 * retries cannot create duplicate system sections.
 */
export const REMINDER_KEY = 'system:reminder';
export const SCHEDULES_KEY = 'system:schedules';

/** One block of the Notes screen: a heading, and the rows under it. */
export interface NoteGroup {
  /**
   * `pinned` is the area at the top, `section` is a group somebody made,
   * `automatic` is durable routine ownership, and `loose` is everything in
   * neither. Only a `section` has controls that maintain it.
   */
  kind: 'pinned' | 'section' | 'automatic' | 'loose';
  /**
   * Stable for the life of the group, and the key its collapsed-or-not is
   * remembered under. A section's is its id, so renaming one does not silently
   * expand it.
   */
  key: string;
  title: string;
  /** The row itself, for a group somebody made. Null for every derived group. */
  section: NoteSection | null;
  notes: Note[];
}

/**
 * The Notes list, split into the blocks the screen draws: pinned, user groups,
 * automatic origin groups, then everything else.
 *
 * WHAT THIS FUNCTION DOES NOT DO IS SORT. `notes` arrives newest-first from the
 * server and keeps that order inside every block; `sections` arrives pinned-first
 * then by name and is walked, never reordered. Both orders are product rules and
 * both live on the server, for the reason `heading` and `meta` do - two clients
 * must not phrase one rule two ways. All this does is decide which block each
 * row falls in, which is drawing.
 *
 * A PINNED NOTE IS LIFTED OUT OF ITS GROUP rather than drawn twice. One note is
 * one row, always: a row that appeared in both the pinned area and its group
 * would make the group's count wrong and give somebody two places to press
 * Delete on one note. Which group it came from is still on the row - the control
 * that moves it says its name - so nothing is hidden by the lift.
 *
 * `searching` changes exactly one thing: an EMPTY GROUP IS DROPPED. With no
 * search a group with nothing in it is still a place - it is where a note is
 * dragged to, and it is the only thing carrying its rename and delete controls -
 * so it is drawn. Under a search it is a heading over nothing, and a screenful
 * of those would bury the matches, so it goes.
 *
 * A note filed in a group that is ARCHIVED (or in one this payload does not
 * carry at all) falls into `loose`. That is deliberate and is the whole of what
 * archiving a group does to the notes inside it: they are drawn where they would
 * be if the group had never existed, and restoring the group takes them back
 * with no write of any kind.
 */
export function groupNotes(
  notes: readonly Note[],
  sections: readonly NoteSection[],
  searching: boolean,
): NoteGroup[] {
  const pinned: Note[] = [];
  const reminders: Note[] = [];
  const scheduled: Note[] = [];
  const loose: Note[] = [];
  const filed = new Map<string, Note[]>();
  for (const section of sections) filed.set(section.id, []);

  for (const note of notes) {
    if (note.pinned) {
      pinned.push(note);
      continue;
    }
    // A routine with no schedule IS a reminder; a routine with one IS a
    // scheduled item. This is the same durable ownership the API reads and
    // writes, never a comparison against a title a person can rename.
    if (note.kind === 'item') {
      (note.schedule === null ? reminders : scheduled).push(note);
      continue;
    }
    const home = note.noteSectionId === null ? undefined : filed.get(note.noteSectionId);
    (home ?? loose).push(note);
  }

  const groups: NoteGroup[] = [];
  if (pinned.length > 0) {
    groups.push({ kind: 'pinned', key: PINNED_KEY, title: 'Pinned', section: null, notes: pinned });
  }
  for (const section of sections) {
    const rows = filed.get(section.id) ?? [];
    if (searching && rows.length === 0) continue;
    groups.push({
      kind: 'section',
      key: section.id,
      title: section.name,
      section,
      notes: rows,
    });
  }
  // Automatic blocks follow the user's own ordered sections and precede the
  // loose list. Empty ones do not exist, so an account is never asked to
  // organise content it does not have. Their fixed order is part of this one
  // model rather than whichever request happened to finish first.
  if (reminders.length > 0) {
    groups.push({
      kind: 'automatic',
      key: REMINDER_KEY,
      title: 'Reminder',
      section: null,
      notes: reminders,
    });
  }
  if (scheduled.length > 0) {
    groups.push({
      kind: 'automatic',
      key: SCHEDULES_KEY,
      title: 'Schedules',
      section: null,
      notes: scheduled,
    });
  }
  if (loose.length > 0) {
    groups.push({
      kind: 'loose',
      key: LOOSE_KEY,
      title: 'Not in a section',
      section: null,
      notes: loose,
    });
  }
  return groups;
}

/**
 * Whether the screen is drawing anything a person would recognise as grouping.
 *
 * THIS IS THE GENERAL-AUDIENCE DEFAULT, in one line. An account with only
 * free-standing notes, no user sections and nothing pinned meets the list it has
 * always had - no headings, no "Not in a section" over everything, nothing to
 * organise before it can be read. Routine notes intentionally make the answer
 * true because their Reminder or Schedules heading explains their ownership.
 */
export function showsGroups(groups: readonly NoteGroup[]): boolean {
  return groups.some((group) => group.kind !== 'loose');
}

/**
 * The count under the editor, which is the whole of the "word count" the design
 * asks for.
 *
 * It cannot come from the server: it changes on every keystroke, and a figure
 * that waited for a round trip would be a figure that was wrong most of the
 * time. That is the test for what belongs in this file.
 *
 * IT COUNTS WORDS, NOT LINE-LEADING MARKERS. `- [ ] call the bank` is three
 * words after its task syntax is removed. `asProse` takes each line's recognized
 * outer marker off first, and the count is of what is left.
 */
export function wordCount(body: string): number {
  const trimmed = asProse(body).trim();
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
}

/** `1 word` / `12 words`, so no caller assembles a plural of its own. */
export function wordsLabel(body: string): string {
  const n = wordCount(body);
  return `${n} ${n === 1 ? 'word' : 'words'}`;
}

/** What an editor is holding, before it is anything the server has. */
export interface Draft {
  title: string;
  body: string;
}

/**
 * What the editor hands back on its way out, when it wrote anything at all.
 *
 * `id` and `heading` name what the last write left on the server, which is what
 * the toast says. `opened` is the row the editor was handed rather than the one
 * it leaves behind. An emptied item has already reported its removal when the
 * write landed, so the closing list uses this kind to avoid replacing that
 * outcome with a generic save. Null for a note the editor made itself.
 */
export interface Saved {
  id: string;
  heading: string;
  opened: Note | null;
}

export function sameDraft(a: Draft, b: Draft): boolean {
  return a.title === b.title && a.body === b.body;
}

/**
 * Whether a draft is worth sending at all.
 *
 * The one thing it decides: a new note is not WRITTEN until there is something
 * in it. The editor opens on a draft rather than on a row, so opening New note
 * and changing your mind leaves nothing behind - and a list full of "Untitled
 * note" rows nobody made is the "written successfully, appears as nothing"
 * shape this client keeps being caught by.
 *
 * It is about when to send a request, not about what a note is called: the
 * server has its own answer to the latter and sends it as `heading`.
 */
export function worthSaving(draft: Draft): boolean {
  return draft.title.trim() !== '' || draft.body.trim() !== '';
}

/**
 * What the editor should be holding for a row, whichever kind it is.
 *
 * An ITEM note has no title of its own to edit: `title` is the item's, it is
 * shown and not typed in, and renaming somebody's gym session from the Notes
 * screen because they tidied a heading is not something this screen may do. So
 * only the body travels, and `saveDraft` sends only the body back.
 */
export function draftOf(note: Note | null): Draft {
  if (note === null) return { title: '', body: '' };
  return { title: note.kind === 'item' ? '' : note.title, body: note.body };
}
